import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';

/** Quanto tempo seguir reposicionando depois da primeira chegada. */
const JANELA_DE_AJUSTE = 2500;
/** Desvio tolerado antes de corrigir de novo, em pixels. */
const TOLERANCIA = 4;

/**
 * Ao trocar de rota a página volta ao topo — menos quando a URL traz
 * âncora. Desde que /sobre e /faq viraram seções da home, chegar por
 * /#duvidas é navegação normal, e mandar para o topo faria o link
 * parecer quebrado.
 *
 * O hash entra nas dependências: ir de /#sobre para /#duvidas não muda
 * o pathname, e sem ele a segunda âncora seria ignorada.
 *
 * Rolar uma vez não basta. As capas do acervo e as fotos da galeria
 * entram com `loading="lazy"` e sem altura reservada, então o conteúdo
 * acima do alvo cresce depois que a rolagem já aconteceu e a seção
 * escorrega para fora da tela — em produção o /faq parava com a seção
 * 1194px abaixo do topo. Por isso o alvo é reposicionado enquanto a
 * página ainda estiver assentando, e qualquer rolagem da leitora
 * cancela o ajuste na hora.
 */
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  useLayoutEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      return undefined;
    }

    const id = hash.slice(1);
    let quadro = null;
    let observador = null;
    let encerrar = null;
    let ultimoTopo = null;
    let vivo = true;

    const parar = () => {
      vivo = false;
      if (quadro) window.cancelAnimationFrame(quadro);
      if (observador) observador.disconnect();
      if (encerrar) window.clearTimeout(encerrar);
      window.removeEventListener('wheel', aoMexer);
      window.removeEventListener('touchstart', aoMexer);
      window.removeEventListener('keydown', aoMexer);
    };

    // A leitora mandou mais que o ajuste: some do caminho.
    function aoMexer() {
      parar();
    }

    const posicionar = () => {
      if (!vivo) return false;
      const alvo = document.getElementById(id);
      if (!alvo) return false;

      const topo = Math.round(window.scrollY + alvo.getBoundingClientRect().top);
      if (ultimoTopo === null || Math.abs(topo - ultimoTopo) > TOLERANCIA) {
        window.scrollTo({ top: topo, left: 0, behavior: 'instant' });
        ultimoTopo = topo;
      }
      return true;
    };

    // O alvo pode não existir no primeiro quadro: a home ainda monta.
    const procurar = (restantes) => {
      if (!vivo) return;
      if (posicionar()) {
        // Achou. Agora é só acompanhar o assentamento.
        observador = new ResizeObserver(posicionar);
        observador.observe(document.body);
        encerrar = window.setTimeout(parar, JANELA_DE_AJUSTE);
        return;
      }
      if (restantes > 0) {
        quadro = window.requestAnimationFrame(() => procurar(restantes - 1));
      }
    };

    window.addEventListener('wheel', aoMexer, { passive: true });
    window.addEventListener('touchstart', aoMexer, { passive: true });
    window.addEventListener('keydown', aoMexer);

    procurar(60);

    return parar;
  }, [pathname, hash]);

  return null;
};

export default ScrollToTop;
